/**
 * Device management API client
 */

import { fetchWithAuth } from './authClient.js';
import { AUTH_API_URL } from './config.js';

export interface Device {
  id: string;
  profile_id: string;
  device_name: string | null;
  status: 'pending' | 'approved' | 'revoked';
  first_seen_at: string;
  approved_at: string | null;
  approved_by_device_id: string | null;
  last_seen_at: string;
}

/**
 * Register or update this device for the current profile.
 * Idempotent — safe to call on every login and app startup.
 * Returns the device status after registration.
 */
export async function registerDevice(deviceId: string): Promise<{
  status: 'pending' | 'approved' | 'revoked';
  is_new: boolean;
}> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/devices/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ device_id: deviceId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error((error as any).error || 'Failed to register device');
  }

  const data = await response.json();
  return { status: data.device.status, is_new: data.is_new };
}

/**
 * List all devices for current profile
 */
export async function listDevices(): Promise<Device[]> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/devices`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to list devices');
  }

  const result = await response.json();
  return result.devices;
}

/**
 * Approve a pending device
 * Note: Caller must also share workspace seeds after approval
 */
export async function approveDevice(deviceId: string): Promise<void> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/devices/${deviceId}/approve`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to approve device');
  }
}

/**
 * Revoke a device
 */
export async function revokeDevice(deviceId: string): Promise<void> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/devices/${deviceId}/revoke`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to revoke device');
  }
}

/**
 * Permanently delete a device record and its associated keys/seeds
 */
export async function deleteDevice(deviceId: string): Promise<void> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/devices/${deviceId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete device');
  }
}

/**
 * Get device status (for polling)
 */
export async function getDeviceStatus(deviceId: string): Promise<{
  status: 'pending' | 'approved' | 'revoked';
  approved_at: string | null;
}> {
  const response = await fetchWithAuth(`${AUTH_API_URL}/api/devices/${deviceId}/status`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get device status');
  }

  return response.json();
}
